import { useState, type DragEvent } from "react";
import uploadIcon from "../assets/icons/ds/icon-upload.svg?raw";
import uploadBtn from "../assets/icons/ds/icon-upload-btn.svg?raw";
import sidebarSimple from "../assets/icons/sidebar-simple.svg";
import { DsIcon } from "../components/DsIcon";
import { useShell } from "../components/ShellContext";
import { Button } from "../components/ds";
import { dsControl } from "../components/ds/styles";
import { cx } from "../lib/cx";
import { settingsPageStyle } from "./settingsChrome";

function copy(locale: string) {
  const tr = locale === "tr";
  return {
    title: tr ? "Bir dosyayla başlayın" : "Start with a file",
    lede: tr
      ? "STEP, DXF, DWG veya PDF bırakın. Teklif verilip verilemeyeceğini kontrol ederiz."
      : "Drop a STEP, DXF, DWG or PDF. We check whether it can be quoted.",
    drop: tr ? "Çizimi buraya bırakın" : "Drop a drawing here",
    sample: tr ? "Örnek dosyayı dene" : "Try a sample file",
    choose: tr ? "Dosya seç" : "Choose files",
    steps: tr ? ["Dosya yükle", "BOM ve maliyet", "Teklif"] : ["Upload file", "BOM & cost", "Quote"],
  };
}

interface Props {
  locale: string;
  onChooseFiles: () => void;
  onSample: () => void;
  onFiles: (files: File[]) => void;
  uploads?: { id: string; name: string; state: string; message: string }[];
}

export function HomeFirstRun({ locale, onChooseFiles, onSample, onFiles, uploads = [] }: Props) {
  const shell = useShell();
  const text = copy(locale);
  const [drag, setDrag] = useState(false);

  function takeDrop(event: DragEvent) {
    event.preventDefault();
    setDrag(false);
    const files = event.dataTransfer?.files ? [...event.dataTransfer.files] : [];
    if (files.length > 0) onFiles(files);
  }

  return (
    <div
      data-theme="light"
      className="sc-home relative font-ds -mx-5 -mb-5 flex h-full min-h-0 flex-1 items-center justify-center bg-fixed px-6 py-8 text-ds-ink max-[900px]:-mx-3 max-[900px]:px-4"
      style={settingsPageStyle}
      data-testid="home"
      data-mode="empty"
    >
      {shell.narrow ? (
        <button
          type="button"
          className={cx(
            dsControl,
            "absolute top-4 left-4 inline-flex size-[38px] items-center justify-center rounded-full bg-ds-glass p-0",
          )}
          aria-label={shell.sidebarOpen ? "Close menu" : "Open menu"}
          data-testid="menu"
          onClick={shell.toggleSidebar}
        >
          <img src={sidebarSimple} alt="" className="size-5" />
        </button>
      ) : null}

      <section
        className={cx(
          "w-full max-w-[480px] rounded-ds-card bg-ds-glass px-8 py-10 text-center shadow-ds-card backdrop-blur-ds backdrop-saturate-[1.1]",
          "[@media(max-height:820px)]:px-6 [@media(max-height:820px)]:py-7",
        )}
        aria-labelledby="home-empty-h"
      >
        <h1 id="home-empty-h" className="m-0 font-ds text-[28px] leading-[1.15] font-semibold text-ds-teal" data-testid="page-heading">
          {text.title}
        </h1>
        <p className="m-0 mx-auto mt-2 max-w-[360px] text-ds-body leading-5 text-ds-ink-3">{text.lede}</p>

        <div
          className={cx(
            "mt-8 flex flex-col items-center gap-4 rounded-[20px] border border-dashed px-6 py-10",
            drag ? "border-ds-amber bg-ds-amber-soft/40" : "border-ds-hair",
            "[@media(max-height:820px)]:mt-5 [@media(max-height:820px)]:py-7",
          )}
          data-testid="home-dropzone"
          onDragEnter={(event) => {
            event.preventDefault();
            setDrag(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={takeDrop}
        >
          <span className="inline-flex size-11 items-center justify-center rounded-[14px] bg-ds-glass-strong text-ds-ink">
            <DsIcon svg={uploadIcon} className="size-5" />
          </span>
          <p className="m-0 text-ds-item font-medium text-ds-ink">{text.drop}</p>
          <span className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="secondary" data-testid="home-sample" onClick={onSample}>
              {text.sample}
            </Button>
            <Button variant="primary" data-testid="home-choose-files" onClick={onChooseFiles}>
              <DsIcon svg={uploadBtn} className="size-4 text-ds-on-amber" />
              {text.choose}
            </Button>
          </span>
        </div>

        {uploads.length > 0 ? (
          <ul className="m-0 mt-4 flex list-none flex-col gap-1 p-0 text-ds-body" data-testid="home-uploads">
            {uploads.slice(0, 3).map((row) => (
              <li key={row.id} className="truncate text-ds-ink-2" data-state={row.state}>
                {row.name}
                <span className="text-ds-ink-3"> {row.message}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <ol className="m-0 mt-8 flex list-none flex-wrap items-center justify-center gap-x-2 gap-y-1 p-0 text-ds-body text-ds-ink-3" data-testid="home-flow">
          {text.steps.map((step, index) => (
            <li key={step} className="inline-flex items-center gap-2">
              <span>{step}</span>
              {index < text.steps.length - 1 ? <span aria-hidden="true">→</span> : null}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
